import {
    Platform
} from "react-native";
import type {
    SidebarShortcutMatchConstantType,
    SidebarAriaShortcutConstantType,
    SidebarButtonPressConstantType,
    SidebarOverlaySideConstantType,
    SidebarBadgeTextConstantType,
    SidebarPersistConstantType,
    SidebarLinkConstantType,
    SidebarState,
    SidebarSide
} from "./type";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    formatNumber
} from "../../helpers/locale";

export const getSidebarActionRadius = ({
    radiuses,
    outer,
    inset
}: {
    radiuses: NCoreUIKit.Radiuses;
    outer: number;
    inset: number;
}): number => {
    return Math.max(radiuses.xs, concentric({
        role: radiuses.actions,
        outer,
        inset
    }));
};

export const SIDEBAR_DEFAULT_SHORTCUT = [
    "mod",
    "b"
];

export const SIDEBAR_PERSIST_VALUES: Record<SidebarState, string> = {
    collapsed: "collapsed",
    expanded: "expanded"
};

const SIDEBAR_MODIFIER_KEYS = [
    "shift",
    "ctrl",
    "mod",
    "alt"
];

const SIDEBAR_EDITABLE_TAGS = [
    "TEXTAREA",
    "SELECT",
    "INPUT"
];

const SIDEBAR_EDITABLE_ROLES = [
    "searchbox",
    "combobox",
    "textbox"
];

export const getIsSidebarEditableTarget = (target: EventTarget | null): boolean => {
    if(typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) {
        return false;
    }

    return target.isContentEditable || SIDEBAR_EDITABLE_TAGS.includes(target.tagName) || SIDEBAR_EDITABLE_ROLES.includes(target.getAttribute("role") ?? "");
};

export const getIsSidebarShortcutMatch = ({
    shortcut,
    event
}: SidebarShortcutMatchConstantType): boolean => {
    const keys = shortcut.map((key) => key.toLowerCase());

    const mainKey = keys.find((key) => !SIDEBAR_MODIFIER_KEYS.includes(key));

    if(!mainKey || !event.key || event.key.toLowerCase() !== mainKey) {
        return false;
    }

    const isMod = keys.includes("mod");

    const isCtrl = keys.includes("ctrl");

    const isCommandOrControl = event.metaKey || event.ctrlKey;

    if(isMod && !isCommandOrControl) {
        return false;
    }

    if(!isMod && (event.metaKey || event.ctrlKey !== isCtrl)) {
        return false;
    }

    return event.altKey === keys.includes("alt") && event.shiftKey === keys.includes("shift");
};

export const getSidebarPersistedOpen = ({
    persistKey
}: SidebarPersistConstantType): boolean | undefined => {
    if(Platform.OS !== "web" || !persistKey) {
        return undefined;
    }

    try {
        const value = window.localStorage.getItem(persistKey);

        if(value === SIDEBAR_PERSIST_VALUES.expanded) {
            return true;
        }

        return value === SIDEBAR_PERSIST_VALUES.collapsed ? false : undefined;
    } catch {
        return undefined;
    }
};

export const setSidebarPersistedOpen = ({
    persistKey,
    isOpen
}: SidebarPersistConstantType & {
    isOpen: boolean;
}) => {
    if(Platform.OS !== "web" || !persistKey) {
        return;
    }

    try {
        window.localStorage.setItem(persistKey, isOpen ? SIDEBAR_PERSIST_VALUES.expanded : SIDEBAR_PERSIST_VALUES.collapsed);
    } catch {
        return;
    }
};

export const openSidebarLink = ({
    href
}: SidebarLinkConstantType) => {
    if(Platform.OS !== "web" || !href) {
        return;
    }

    window.location.assign(href);
};

export const pressSidebarButton = ({
    onPress,
    href
}: SidebarButtonPressConstantType) => {
    if(onPress) {
        onPress();

        return;
    }

    openSidebarLink({
        href
    });
};

export const getSidebarOverlaySide = ({
    direction,
    side
}: SidebarOverlaySideConstantType): SidebarSide => {
    return (side === "left") === (direction === "ltr") ? "right" : "left";
};

export const getSidebarBadgeText = ({
    locale,
    count,
    max
}: SidebarBadgeTextConstantType): string => {
    if(count > max) {
        return `${formatNumber({
            value: max,
            locale
        })}+`;
    }

    return formatNumber({
        value: count,
        locale
    });
};

export const getSidebarAriaShortcut = ({
    shortcut
}: SidebarAriaShortcutConstantType): string | undefined => {
    if(!shortcut) {
        return undefined;
    }

    const keys = shortcut.map((key) => key === "mod" ? "mod" : key.toLowerCase() === "ctrl" ? "Control" : `${key.charAt(0).toUpperCase()}${key.slice(1)}`);

    if(!keys.includes("mod")) {
        return keys.join("+");
    }

    return [
        "Meta",
        "Control"
    ].map((modifier) => keys.map((key) => key === "mod" ? modifier : key).join("+")).join(" ");
};
